import { useState, FormEvent } from 'react'
import { useLocation } from 'react-router-dom'
import { Logo } from '../components/Logo'
import { apiPost, ApiError } from '../lib/api'

export default function Login() {
  const location = useLocation()
  // ProtectedRoute passes state={{ from: location }} when it redirects here.
  // Extract install_id so the magic link URL carries it cross-browser.
  const fromSearch = (location.state as { from?: { search?: string } } | null)?.from?.search
  const installId = fromSearch ? new URLSearchParams(fromSearch).get('install_id') ?? undefined : undefined

  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await apiPost('/api/auth/magic-link', installId ? { email, installId } : { email })
      setSent(true)
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError('Error al enviar el link. Intentá de nuevo.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg, #e8f4fd 0%, #f4f7fa 50%, #e8f0f8 100%)' }}
    >
      <div className="card w-full max-w-md p-8 shadow-lg">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <Logo size="lg" />
        </div>

        {sent ? (
          /* Success state */
          <div className="text-center">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: 'color-mix(in srgb, var(--success) 15%, transparent)' }}
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ color: 'var(--success)' }}
                aria-hidden="true"
              >
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--foreground)' }}>
              Revisá tu email
            </h2>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--muted)' }}>
              Te enviamos un link para acceder a{' '}
              <span className="font-medium" style={{ color: 'var(--foreground)' }}>
                {email}
              </span>
              . El link expira en 15 minutos.
            </p>
            <button
              className="mt-6 text-sm underline underline-offset-2"
              style={{ color: 'var(--primary)' }}
              onClick={() => { setSent(false); setEmail('') }}
            >
              Usar otro email
            </button>
          </div>
        ) : (
          /* Form state */
          <>
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--foreground)' }}>
                Accedé al Llamador
              </h1>
              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                Ingresá tu email y te enviamos un link de acceso
              </p>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium mb-1.5"
                  style={{ color: 'var(--foreground)' }}
                >
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  className="input"
                  placeholder="tu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  autoFocus
                  autoComplete="email"
                />
              </div>

              {error && (
                <p className="text-sm text-center" style={{ color: 'var(--error)' }}>
                  {error}
                </p>
              )}

              <button
                type="submit"
                className="btn-primary w-full py-3 mt-2"
                disabled={loading || !email.trim()}
              >
                {loading ? (
                  <>
                    <span
                      className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin"
                      style={{ borderColor: 'rgba(255,255,255,0.4)', borderTopColor: 'white' }}
                    />
                    Enviando...
                  </>
                ) : (
                  'Enviar link'
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}
